'use client';

import React, { useState, useEffect } from 'react';
import { Plus, Upload, X } from 'lucide-react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DatePicker } from '@/components/ui/date-picker';
import { toast } from '@/components/ui/use-toast';
import api from '@crm-src/lib/api';
import { dealsApi, type Deal, type CreateDealData } from '@crm-src/lib/deals-api';

interface HaveInvoiceFormProps {
  onBack: () => void;
  onCancel: () => void;
  onSubmit: (invoiceData: any, file?: File) => void;
  isCreating: boolean;
}

export const HaveInvoiceForm: React.FC<HaveInvoiceFormProps> = ({
  onBack,
  onCancel,
  onSubmit,
  isCreating
}) => {
  // Form state
  const [newInvoice, setNewInvoice] = useState({
    contact_id: null as number | null,
    invoice_number: '',
    total_amount: '' as string | number,
    issue_date: (() => {
      const date = new Date();
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    })(),
    due_date: (() => {
      const date = new Date();
      date.setDate(date.getDate() + 30);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    })(),
  });

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  // Customer/Contact state
  const [contacts, setContacts] = useState<any[]>([]);
  const [selectedContact, setSelectedContact] = useState<any | null>(null);

  // Customer deals state
  const [customerDeals, setCustomerDeals] = useState<Deal[]>([]);
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [showCreateDealDialog, setShowCreateDealDialog] = useState(false);
  const [isLoadingDeals, setIsLoadingDeals] = useState(false);
  const [newDeal, setNewDeal] = useState<CreateDealData>({
    customer_id: '',
    title: '',
    description: '',
    amount: 0,
    deal_type: 'simple_purchase'
  });

  // Helper function to format customer display consistently
  const formatCustomerDisplay = (contact: any) => {
    const name = contact.full_name || contact.name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unnamed';
    const company = contact.company_name ? ` • ${contact.company_name}` : '';
    return `${name}${company} (${contact.email})`;
  };

  // Component for two-row customer display
  const CustomerDisplayTwoRows = ({ contact }: { contact: any }) => {
    const name = contact.full_name || contact.name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unnamed';
    const company = contact.company_name ? ` • ${contact.company_name}` : '';

    return (
      <Box sx={{ display: "flex", flexDirection: "column", py: 0.5, textAlign: "left" }}>
        <Box sx={{ fontWeight: 500, fontSize: 14, textAlign: "left" }}>{name}{company}</Box>
        <Box sx={{ fontSize: 12, color: "text.secondary", textAlign: "left" }}>{contact.email}</Box>
      </Box>
    );
  };

  // Component for deal display
  const DealDisplay = ({ deal }: { deal: any }) => {
    return (
      <Box sx={{ textAlign: "left" }}>
        <Typography component="span" sx={{ fontWeight: 500, fontSize: 14 }}>{deal.name}</Typography>
      </Box>
    );
  };

  // Load contacts for dropdown
  const loadContacts = async () => {
    try {
      // Use customers endpoint since Tenant has_many :customers. The dropdown
      // needs only id/name/company/email — the selected customer's deals are
      // fetched separately (loadCustomerDeals) — so request the LIGHT payload
      // (include_deals=false) and skip the heavy per-row deals/invoices/files
      // serialization. See crm-api customers#index + generate-invoice-form.
      const response = await api.get('/customers?include_deals=false');

      // Try different possible data structures - ApiResponse format first
      let contacts = response.data?.data || response.data?.customers || response.data?.contacts || response.data || [];

      // Ensure contacts is an array
      if (!Array.isArray(contacts)) {
        console.warn('Contacts data is not an array:', contacts);
        contacts = [];
      }

      setContacts(contacts);
    } catch (error) {
      console.error('Error loading contacts:', error);
      toast({
        title: 'Error',
        description: 'Failed to load customers',
        variant: 'destructive'
      });
    }
  };

  // Load customer deals for selected customer
  const loadCustomerDeals = async (customerId: string) => {
    try {
      setIsLoadingDeals(true);
      const response = await dealsApi.getDeals({ customer_id: customerId });
      setCustomerDeals(response.customer_deals || []);
    } catch (error) {
      console.error('Error loading customer deals:', error);
      setCustomerDeals([]);
      toast({
        title: 'Error',
        description: 'Failed to load customer deals',
        variant: 'destructive'
      });
    } finally {
      setIsLoadingDeals(false);
    }
  };

  // Create new customer deal
  const createCustomerDeal = async () => {
    if (!selectedContact || !newDeal.title.trim()) {
      toast({
        title: 'Error',
        description: 'Please select a customer and enter deal name',
        variant: 'destructive'
      });
      return;
    }

    try {
      const dealData = {
        ...newDeal,
        customer_id: selectedContact.id.toString()
      };

      const createdDeal = await dealsApi.createDeal(dealData);

      // Add to deals list and select it
      setCustomerDeals(prev => [createdDeal, ...prev]);
      setSelectedDeal(createdDeal);
      setShowCreateDealDialog(false);

      toast({
        title: 'Success',
        description: 'Customer deal created successfully'
      });
    } catch (error) {
      console.error('Error creating customer deal:', error);
      toast({
        title: 'Error',
        description: 'Failed to create customer deal',
        variant: 'destructive'
      });
    }
  };

  // Handle form submission
  const handleSubmit = () => {
    const totalAmount = parseFloat(newInvoice.total_amount.toString()) || 0;

    if (!selectedContact || !totalAmount || !selectedDeal || !newInvoice.invoice_number.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please select a customer, customer deal, enter invoice number, and enter total amount',
        variant: 'destructive'
      });
      return;
    }

    // Validate file size (10MB limit)
    if (uploadedFile && uploadedFile.size > 10 * 1024 * 1024) {
      toast({
        title: 'File Too Large',
        description: 'File size must be less than 10MB',
        variant: 'destructive'
      });
      return;
    }

    const invoiceData = {
      customer_id: selectedContact.id,
      customer_deal_id: parseInt(selectedDeal.id),
      invoice_number: newInvoice.invoice_number.trim(),
      total_amount: totalAmount,
      issue_date: newInvoice.issue_date,
      due_date: newInvoice.due_date
    };

    onSubmit(invoiceData, uploadedFile || undefined);
  };

  // Load contacts on mount
  useEffect(() => {
    loadContacts();
  }, []);

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Typography component="h2" sx={{ fontSize: "1.125rem", fontWeight: 600, color: "text.primary" }}>Create New Invoice</Typography>
        <Typography sx={{ fontSize: 14, color: "text.secondary", mt: 0.5 }}>
          Fill in the details to create a new invoice
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Customer Selection */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <FormLabel htmlFor="customer" sx={{ display: "block", mb: 0.5 }}>Select Customer *</FormLabel>
            <Select
              id="customer"
              size="small"
              fullWidth
              displayEmpty
              value={selectedContact?.id?.toString() || ''}
              onChange={(e) => {
                const value = e.target.value;
                const contact = contacts.find(c => c.id.toString() === value);
                setSelectedContact(contact || null);
                if (contact) {
                  loadCustomerDeals(contact.id.toString());
                }
              }}
              renderValue={() =>
                selectedContact
                  ? <CustomerDisplayTwoRows contact={selectedContact} />
                  : <Typography component="span" sx={{ color: "text.disabled" }}>Choose a customer</Typography>
              }
              sx={{ minHeight: "3.5rem", width: "100%" }}
            >
              {Array.isArray(contacts) && contacts.length > 0 ? (
                contacts.map((contact) => (
                  <MenuItem key={contact.id} value={contact.id.toString()} sx={{ minHeight: "3.5rem" }}>
                    <CustomerDisplayTwoRows contact={contact} />
                  </MenuItem>
                ))
              ) : (
                <Box sx={{ p: 1, fontSize: 14, color: "text.secondary", textAlign: "center" }}>
                  No customers found
                </Box>
              )}
            </Select>
          </Box>
        </Box>

        {/* Customer Deal Selection */}
        {selectedContact && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <FormLabel htmlFor="customer_deal" sx={{ display: "block", mb: 0.5 }}>Select Customer Deal *</FormLabel>
              {isLoadingDeals ? (
                <Box sx={{ p: 1, fontSize: 14, color: "text.secondary" }}>Loading deals...</Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Select
                      size="small"
                      displayEmpty
                      value={selectedDeal?.id || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        const deal = customerDeals.find(d => d.id === value);
                        setSelectedDeal(deal || null);
                      }}
                      renderValue={(value) => {
                        const deal = customerDeals.find(d => d.id === value);
                        return deal
                          ? <DealDisplay deal={deal} />
                          : <Typography component="span" sx={{ color: "text.disabled" }}>Choose a customer deal</Typography>;
                      }}
                      sx={{ flex: 1 }}
                    >
                      {customerDeals.length > 0 ? (
                        customerDeals.map((deal) => (
                          <MenuItem key={deal.id} value={deal.id}>
                            <DealDisplay deal={deal} />
                          </MenuItem>
                        ))
                      ) : (
                        <Box sx={{ p: 1, fontSize: 14, color: "text.secondary", textAlign: "center" }}>
                          No deals found for this customer
                        </Box>
                      )}
                    </Select>

                    <Button
                      type="button"
                      variant="outlined"
                      color="inherit"
                      onClick={() => setShowCreateDealDialog(true)}
                      startIcon={<Plus size={16} />}
                      sx={{ whiteSpace: "nowrap", height: 40 }}
                    >
                      Create New Deal
                    </Button>
                  </Box>

                  {customerDeals.length === 0 && (
                    <Typography component="span" sx={{ fontSize: 12, color: "text.secondary" }}>
                      No deals found. Create one to attach the invoice to.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Invoice Details */}
        <Divider />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <FormLabel htmlFor="invoice_number" sx={{ display: "block", mb: 0.5 }}>Invoice Number *</FormLabel>
            <TextField
              id="invoice_number"
              type="text"
              size="small"
              fullWidth
              value={newInvoice.invoice_number}
              onChange={(e) => {
                setNewInvoice({
                  ...newInvoice,
                  invoice_number: e.target.value
                });
              }}
              placeholder="Enter invoice number (e.g., INV-2024-001)"
            />
          </Box>
          <Box>
            <FormLabel htmlFor="total_amount" sx={{ display: "block", mb: 0.5 }}>Total Amount *</FormLabel>
            <TextField
              id="total_amount"
              type="number"
              size="small"
              fullWidth
              inputProps={{ step: "0.01" }}
              value={newInvoice.total_amount}
              onChange={(e) => {
                const value = e.target.value;
                setNewInvoice({
                  ...newInvoice,
                  total_amount: value === '' ? '' : (parseFloat(value) || 0)
                });
              }}
              placeholder="0.00"
            />
          </Box>
        </Box>

        {/* Dates */}
        <Divider />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Box>
              <FormLabel sx={{ display: "block", mb: 0.5 }}>Issue Date *</FormLabel>
              <DatePicker
                value={newInvoice.issue_date}
                onChange={(date) => setNewInvoice({ ...newInvoice, issue_date: date })}
              />
            </Box>
            <Box>
              <FormLabel sx={{ display: "block", mb: 0.5 }}>Due Date *</FormLabel>
              <DatePicker
                value={newInvoice.due_date}
                onChange={(date) => setNewInvoice({ ...newInvoice, due_date: date })}
              />
            </Box>
          </Box>
        </Box>

        {/* File Upload */}
        <Divider />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <FormLabel htmlFor="invoice_file" sx={{ display: "block", mb: 0.5 }}>Invoice File (Optional)</FormLabel>
            <Box sx={{ mt: 1 }}>
              <TextField
                id="invoice_file"
                type="file"
                size="small"
                fullWidth
                inputProps={{ accept: ".pdf,.doc,.docx,.jpg,.jpeg,.png" }}
                onChange={(e) => {
                  const file = (e.target as HTMLInputElement).files?.[0] || null;
                  setUploadedFile(file);
                }}
                sx={{ cursor: "pointer" }}
              />
              <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
                Supported formats: PDF, DOC, DOCX, JPG, JPEG, PNG (Max 10MB)
              </Typography>
            </Box>

            {uploadedFile && (
              <Box sx={{ mt: 1.5, p: 1.5, bgcolor: (t) => alpha(t.palette.success.main, 0.12), border: 1, borderColor: "success.light", borderRadius: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ color: "success.main", display: "flex" }}><Upload size={16} /></Box>
                    <Typography component="span" sx={{ fontSize: 14, color: "success.dark" }}>
                      {uploadedFile.name} ({(uploadedFile.size / 1024 / 1024).toFixed(2)} MB)
                    </Typography>
                  </Box>
                  <Button
                    variant="text"
                    color="inherit"
                    size="small"
                    onClick={() => {
                      setUploadedFile(null);
                      // Reset the file input
                      const fileInput = document.getElementById('invoice_file') as HTMLInputElement;
                      if (fileInput) fileInput.value = '';
                    }}
                  >
                    <X size={16} />
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 2 }}>
          <Button variant="outlined" color="inherit" onClick={onBack}>
            Back
          </Button>
          <Button variant="outlined" color="inherit" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={isCreating}>
            {isCreating ? 'Creating...' : 'Create Invoice'}
          </Button>
        </Box>
      </Box>

      {/* Create Customer Deal Dialog */}
      <Dialog open={showCreateDealDialog} onClose={() => setShowCreateDealDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Create New Customer Deal
          <Typography sx={{ fontSize: 14, color: "text.secondary", mt: 0.5 }}>
            Enter a name for the new deal with {selectedContact?.full_name || selectedContact?.name || 'the selected customer'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box>
                <FormLabel htmlFor="deal_title" sx={{ display: "block", mb: 0.5 }}>Deal Name *</FormLabel>
                <TextField
                  id="deal_title"
                  size="small"
                  fullWidth
                  value={newDeal.title}
                  onChange={(e) => setNewDeal({ ...newDeal, title: e.target.value })}
                  placeholder="Enter deal name"
                />
              </Box>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 2 }}>
              <Button variant="outlined" color="inherit" onClick={() => {
                setShowCreateDealDialog(false);
                setNewDeal({
                  customer_id: '',
                  title: '',
                  description: '',
                  amount: 0,
                  deal_type: 'simple_purchase'
                });
              }}>
                Cancel
              </Button>
              <Button variant="contained" onClick={createCustomerDeal}>
                Create Deal
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};
